import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Globe, HelpCircle, Shield, LogOut, Loader2, 
  Trash2, BrainCircuit, MessageSquare, Zap, List, ShieldCheck,
  Server, Coins, CheckCircle2, RefreshCw, Heart, Megaphone,
  Coffee
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { uploadAvatar, uploadBackground, updateNickname, deleteUserAccount } from '../../services/authService';
import { pointService } from '../../services/pointService';
import { fetchUserComments } from '../../services/commentService';
import { fetchUserNewsComments } from '../../services/newsCommentService';
import { fetchUserLeagueSelectionsFromDB, fetchLeagues, saveUserLeagueSelectionsToDB, LeagueDB, fetchTeamInfo } from '../../services/footballService';
import { favoriteService } from '../../services/favoriteService';
import { t, Language } from '../../i18n';
import { ActivityDashboard } from '../MyPage/ActivityDashboard';
import { NoticeListView } from '../MyPage/NoticeListView';
import { LeagueSelector } from '../MyPage/LeagueSelector';
import { PointHistoryView } from '../MyPage/PointHistoryView';
import { AttendanceView } from '../MyPage/AttendanceView';
import { SponsorshipView } from '../MyPage/SponsorshipView';
import { MyPredictionsView } from '../MyPage/MyPredictionsView';
import { ProfileSection } from '../MyPage/ProfileSection';
import { MenuSection } from '../MyPage/MenuSection';
import { EditNicknameModal } from '../MyPage/EditNicknameModal';
import { FavoriteTeamsView } from '../MyPage/FavoriteTeamsView';
import { FavoriteTeamDetailView } from '../MyPage/FavoriteTeamDetailView';
import { DetailViewContainer } from '../MyPage/DetailViewContainer';
import { ConfirmModal } from '../Common/ConfirmModal';
import { AppFooter } from '../Common/AppFooter';
import { FullPageLoader } from '../Common/FullPageLoader';
import SEO from '../Common/SEO';

interface MyPageViewProps {
    onBack: () => void;
    onLogout: () => void;
    initialDetail?: DetailType;
    teamId?: string;
}

type DetailType = 'none' | 'comments' | 'leagues' | 'settings' | 'points' | 'attendance' | 'match_predictions' | 'teams' | 'team_detail' | 'notices' | 'sponsorship';
type FilterPeriodType = 'today' | '7days' | '30days' | 'all';

export const MyPageView: React.FC<MyPageViewProps> = ({ onBack, onLogout, initialDetail = 'none', teamId }) => {
    const { user, logout, updateUser, isLoading: isAuthLoading } = useAuth();
    const { lang, setLang } = useLanguage();
    const { showToast } = useToast();
    
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
    const [isConfirmLogoutOpen, setIsConfirmLogoutOpen] = useState(false);
    const [isUpdatingName, setIsUpdatingName] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);
    const [activeDetail, setActiveDetail] = useState<DetailType>(initialDetail);
    const [tempLang, setTempLang] = useState<Language>(lang as Language);

    useEffect(() => {
        if (initialDetail !== activeDetail) {
            setActiveDetail(initialDetail);
            // 상세 페이지 접근 시 상단으로 스크롤
            if (initialDetail !== 'none') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                // PullToRefresh 내부의 스크롤 컨테이너를 찾아 초기화
                const scrollContainers = document.querySelectorAll('.overflow-y-auto');
                scrollContainers.forEach(container => {
                    container.scrollTop = 0;
                });
            }
        }
    }, [initialDetail, activeDetail]);

    // 언어 설정 진입 시 tempLang 초기화
    useEffect(() => {
        if (activeDetail === 'settings') {
            setTempLang(lang as Language);
        }
    }, [activeDetail, lang]);

    const [userPoints, setUserPoints] = useState<number>(user?.points || 0);
    
    const [filterPeriod, setFilterPeriod] = useState<FilterPeriodType>('all');
    const [userActivities, setUserActivities] = useState<any[]>([]);
    const [allLeagues, setAllLeagues] = useState<LeagueDB[]>([]);
    const [selectedLeagueIds, setSelectedLeagueIds] = useState<string[]>([]);
    const [selectedDefaultTeamId, setSelectedDefaultTeamId] = useState<string | null>(null);
    const [favoriteTeam, setFavoriteTeam] = useState<{ name: string; logo: string } | null>(null);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isSavingLeagues, setIsSavingLeagues] = useState(false);
    const [isSavingDefaultTeam, setIsSavingDefaultTeam] = useState(false);
    const [isSavingLang, setIsSavingLang] = useState(false);

    useEffect(() => {
        if (!isAuthLoading && !user) {
            sessionStorage.setItem('redirectAfterLogin', window.getCurrentPath());
            window.navigateTo('/login');
        }
    }, [user, isAuthLoading]);

    useEffect(() => {
        if (user) {
            pointService.getUserPoints(user.id).then(setUserPoints);
            loadFavoriteTeam();
        }
    }, [user?.id]);

    const loadFavoriteTeam = async () => {
        if (!user) return;
        try {
            const teams = await favoriteService.getFavoriteTeams();
            const defaultTeamSelection = teams.find(t => t.is_favorite);
            if (defaultTeamSelection) {
                const teamInfo = await fetchTeamInfo(defaultTeamSelection.team_id);
                if (teamInfo) {
                    setFavoriteTeam({
                        name: teamInfo.team.name,
                        logo: teamInfo.team.logo
                    });
                }
            } else {
                setFavoriteTeam(null);
            }
        } catch (e) {
            console.error('Error loading favorite team:', e);
        }
    };

    useEffect(() => {
        if (activeDetail === 'comments') {
            loadUserActivities();
        } else if (activeDetail === 'leagues') {
            loadLeaguesData();
        }
    }, [activeDetail]);

    const loadUserActivities = async () => {
        if (!user) return;
        setIsDataLoading(true);
        try {
            const [matchComments, newsComments] = await Promise.all([
                fetchUserComments(user.id),
                fetchUserNewsComments(user.id)
            ]);
            const combined = [
                ...matchComments.map(c => ({ ...c, activityType: 'match' })), 
                ...newsComments.map(c => ({ ...c, activityType: 'news' }))
            ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
            setUserActivities(combined);
        } catch (e) { console.error(e); } finally { setIsDataLoading(false); }
    };

    const loadLeaguesData = async () => {
        if (!user) return;
        setIsDataLoading(true);
        try {
            const [selections, leagues] = await Promise.all([fetchUserLeagueSelectionsFromDB(user.id), fetchLeagues()]);
            setAllLeagues(leagues);
            setSelectedLeagueIds(selections.map(s => s.league_id.toUpperCase()));
        } catch (e) { console.error(e); } finally { setIsDataLoading(false); }
    };

    const handleUpdateName = useCallback(async (newName: string) => {
        if (!user) return;
        const trimmed = newName.trim();
        if (trimmed === user.name) { setIsEditingName(false); return; }
        if (trimmed.length < 1 || trimmed.length > 20) { setNameError(t(lang as Language, 'nicknameError')); return; }
        setIsUpdatingName(true);
        try {
            await updateNickname(user.id, trimmed);
            updateUser({ name: trimmed });
            setIsEditingName(false);
            setNameError(null);
            showToast('success', t(lang as Language, 'nicknameUpdated') || 'Nickname updated');
        } catch (e) { 
            showToast('error', t(lang as Language, 'failedToUpdateNickname'));
        } finally { setIsUpdatingName(false); }
    }, [user, lang, updateUser, showToast]);

    const handleLogout = useCallback(async () => {
        setIsConfirmLogoutOpen(false);
        try { await logout(); onLogout(); } catch (e) { console.error(e); }
    }, [logout, onLogout]);

    const handleDeleteAccount = useCallback(async () => {
        if (!user) return;
        setIsDeletingAccount(true);
        try {
            await deleteUserAccount(user.id);
            onLogout();
        } catch (e: any) { alert(e.message || "Failed to delete account"); } 
        finally { setIsDeletingAccount(false); setIsConfirmDeleteOpen(false); }
    }, [user, onLogout]);

    const handleAvatarChange = useCallback(async (file: File) => {
        if (!user) return;
        if (file.size > 5 * 1024 * 1024) {
            showToast('error', t(lang as Language, 'fileSizeError'));
            return;
        }
        try {
            const url = await uploadAvatar(user.id, file);
            updateUser({ avatar: url });
            showToast('success', t(lang as Language, 'profileImageUpdated'));
        } catch (e: any) { 
            showToast('error', e.message || t(lang as Language, 'uploadFailed')); 
        }
    }, [user, lang, updateUser, showToast]);

    const handleBackgroundChange = useCallback(async (file: File) => {
        if (!user) return;
        if (file.size > 5 * 1024 * 1024) {
            showToast('error', t(lang as Language, 'fileSizeUnder5MB'));
            return;
        }
        try {
            const url = await uploadBackground(user.id, file);
            updateUser({ backgroundUrl: url });
            showToast('success', t(lang as Language, 'backgroundImageUpdated'));
        } catch (e: any) { 
            showToast('error', e.message || t(lang as Language, 'uploadFailed')); 
        }
    }, [user, lang, updateUser, showToast]);

    const handleSaveLeagues = useCallback(async () => {
        if (!user) return;
        setIsSavingLeagues(true);
        try {
            const selections = selectedLeagueIds.map((id, index) => ({ league_id: id, order: index }));
            await saveUserLeagueSelectionsToDB(user.id, selections);
            showToast('success', t(lang as Language, 'syncSuccess'));
            setActiveDetail('none');
            window.navigateTo('/mypage');
        } catch (e) {
            showToast('error', t(lang as Language, 'saveFailed'));
        } finally {
            setIsSavingLeagues(false);
        }
    }, [user, selectedLeagueIds, lang, showToast]);

    const handleSaveLanguage = useCallback(async () => {
        setIsSavingLang(true);
        try {
            setLang(tempLang);
            setActiveDetail('none');
            window.navigateTo('/mypage');
        } catch (e) {
            showToast('error', t(lang as Language, 'saveFailed'));
        } finally {
            setIsSavingLang(false);
        }
    }, [tempLang, setLang, showToast, lang]);

    const handleSaveDefaultTeam = useCallback(async () => {
        if (!selectedDefaultTeamId) return;
        setIsSavingDefaultTeam(true);
        try {
            const { favoriteService } = await import('../../services/favoriteService');
            const success = await favoriteService.toggleFavoriteTeam(selectedDefaultTeamId, true);
            if (success) {
                showToast('success', t(lang as Language, 'syncSuccess'));
                loadFavoriteTeam();
                // Trigger internal refresh if view is active
                if ((window as any).refreshFavoriteTeams) {
                    await (window as any).refreshFavoriteTeams();
                }
            }
        } catch (e) {
            showToast('error', t(lang as Language, 'saveFailed'));
        } finally {
            setIsSavingDefaultTeam(false);
        }
    }, [selectedDefaultTeamId, lang, showToast]);

    const adminMenu = useMemo(() => [
        { 
            icon: ShieldCheck, 
            label: t(lang as Language, 'adminControlCenter'), 
            subLabel: t(lang as Language, 'adminControlCenterDesc'), 
            href: '/admin' 
        }
    ], [lang]);

    const agentMenu = useMemo(() => {
        const items = [
            { 
                icon: Megaphone, 
                label: t(lang as Language, 'notices'), 
                subLabel: t(lang as Language, 'noNoticesDesc'), 
                onClick: () => { window.navigateTo('/mypage/notices'); }
            },
            { 
                icon: Zap, 
                label: t(lang as Language, 'attendance'), 
                subLabel: t(lang as Language, 'attendanceDesc'), 
                onClick: () => { window.navigateTo('/mypage/attendance'); }
            },
            { icon: List, label: t(lang as Language, 'favoriteLeagues'), subLabel: t(lang as Language, 'favoriteLeaguesDesc'), onClick: () => { window.navigateTo('/mypage/leagues'); } },
            { icon: Heart, label: t(lang as Language, 'favoriteTeams'), subLabel: t(lang as Language, 'favoriteTeamsDesc'), onClick: () => { window.navigateTo('/mypage/teams'); } },
            { icon: BrainCircuit, label: t(lang as Language, 'matchPredictions'), subLabel: t(lang as Language, 'matchPredictionsDesc'), onClick: () => { window.navigateTo('/mypage/predictions'); } },
            { 
                icon: Coins, 
                label: t(lang as Language, 'pointHistoryTitle'), 
                subLabel: t(lang as Language, 'pointHistorySub'), 
                onClick: () => { window.navigateTo('/mypage/points'); }
            },
            { icon: MessageSquare, label: t(lang as Language, 'recentActivity'), subLabel: t(lang as Language, 'recentActivityDesc'), onClick: () => { window.navigateTo('/mypage/activities'); } },
        ];

        // 후원하기 메뉴는 관리자에게만 노출
        if (user?.role === 'admin') {
            items.push({ 
                icon: Coffee, 
                label: t(lang as Language, 'sponsorship'), 
                subLabel: t(lang as Language, 'supportXlevenService'), 
                onClick: () => { window.navigateTo('/mypage/sponsorship'); } 
            });
        }

        return items;
    }, [lang, user?.role]);

    const supportMenu = useMemo(() => [
        { icon: HelpCircle, label: t(lang as Language, 'helpCenter'), href: '/help' },
        { icon: Server, label: t(lang as Language, 'apiAndCopyright'), href: '/api-info' },
        { icon: Shield, label: t(lang as Language, 'privacyPolicy'), href: '/privacy' }
    ], [lang]);

    const [isConfirmClearOpen, setIsConfirmClearOpen] = useState(false);
    const [isClearingCache, setIsClearingCache] = useState(false);

    const handleClearLocalStorage = useCallback(() => {
        setIsConfirmClearOpen(false);
        setIsClearingCache(true);
        // storageService의 clearCache를 사용하여 xleven 관련 데이터만 안전하게 삭제
        import('../../services/storageService').then(m => {
            m.clearCache();
            setTimeout(() => {
                setIsClearingCache(false);
            }, 2000);
        });
    }, []);

    const accountMenu = useMemo(() => [
        { 
            icon: Globe, 
            label: t(lang as Language, 'language'), 
            subLabel: lang === 'ko' ? '한국어 (KR)' : lang === 'ja' ? '日本語 (JP)' : lang === 'es' ? 'Español (ES)' : lang === 'fr' ? 'Français (FR)' : 'English (UK)', 
            onClick: () => { window.navigateTo('/mypage/language'); } 
        },
        { icon: Trash2, label: t(lang as Language, 'clearLocalStorage'), onClick: () => setIsConfirmClearOpen(true) },
        { icon: LogOut, label: t(lang as Language, 'logOutAccount'), onClick: () => setIsConfirmLogoutOpen(true) }
    ], [lang, setIsConfirmLogoutOpen, setIsConfirmClearOpen]);

    if (!user) return null;

    const getSeoMetadata = () => {
        const baseTitle = t(lang as Language, 'mypage');
        const baseDesc = t(lang as Language, 'myPageDesc');

        switch (activeDetail) {
            case 'attendance':
                return {
                    title: t(lang as Language, 'attendance'),
                    description: t(lang as Language, 'attendanceDescSEO')
                };
            case 'points':
                return {
                    title: t(lang as Language, 'pointHistory'),
                    description: t(lang as Language, 'pointHistoryDescSEO')
                };
            case 'match_predictions':
                return {
                    title: t(lang as Language, 'matchPredictions'),
                    description: t(lang as Language, 'matchPredictionsDescSEO')
                };
            case 'comments':
                return {
                    title: t(lang as Language, 'recentActivity'),
                    description: t(lang as Language, 'recentActivityDescSEO')
                };
            case 'leagues':
                return {
                    title: t(lang as Language, 'favoriteLeagues'),
                    description: t(lang as Language, 'favoriteLeaguesDescSEO')
                };
            case 'teams':
            case 'team_detail':
                return {
                    title: t(lang as Language, 'favoriteTeams'),
                    description: t(lang as Language, 'favoriteTeamsDescSEO')
                };
            case 'settings':
                return {
                    title: t(lang as Language, 'language'),
                    description: t(lang as Language, 'languageDescSEO')
                };
            case 'notices':
                return {
                    title: t(lang as Language, 'notices'),
                    description: t(lang as Language, 'noNoticesDesc')
                };
            case 'sponsorship':
                return {
                    title: t(lang as Language, 'sponsorship'),
                    description: t(lang as Language, 'sponsorshipDesc')
                };
            default:
                return {
                    title: baseTitle,
                    description: baseDesc
                };
        }
    };

    const seo = getSeoMetadata();

    return (
        <div className="min-h-screen bg-white flex flex-col relative">
            <SEO title={seo.title} description={seo.description} canonical={`${window.location.origin}/mypage${activeDetail !== 'none' ? `/${activeDetail.replace('_', '-')}` : ''}`} />
            {isClearingCache && (
                <div className="fixed inset-0 z-[100]">
                    <FullPageLoader 
                        isLoading={true} 
                        icon={RefreshCw} 
                        text={t(lang as Language, 'clearingData')} 
                    />
                </div>
            )}

            <ConfirmModal 
                isOpen={isConfirmDeleteOpen} 
                onClose={() => setIsConfirmDeleteOpen(false)} 
                onConfirm={handleDeleteAccount} 
                isLoading={isDeletingAccount}
                title={t(lang as Language, 'deleteAccount')} 
                description={t(lang as Language, 'deleteAccountConfirm')}
                confirmLabel={t(lang as Language, 'delete')}
            />

            <ConfirmModal 
                isOpen={isConfirmClearOpen} 
                onClose={() => !isClearingCache && setIsConfirmClearOpen(false)} 
                onConfirm={handleClearLocalStorage} 
                isLoading={isClearingCache}
                title={t(lang as Language, 'clearLocalStorage')} 
                description={t(lang as Language, 'clearLocalStorageConfirm')}
                confirmLabel={t(lang as Language, 'delete')}
            />

            <ConfirmModal 
                isOpen={isConfirmLogoutOpen} 
                onClose={() => setIsConfirmLogoutOpen(false)} 
                onConfirm={handleLogout} 
                title={t(lang as Language, 'logOutAccount')} 
                description={t(lang as Language, 'logoutConfirm')}
                confirmLabel={t(lang as Language, 'confirm')}
                cancelLabel={t(lang as Language, 'cancel')}
            />

            {activeDetail === 'none' ? (
                <>
                    <div className="flex-1">
                        <ProfileSection 
                            user={user} 
                            userPoints={userPoints} 
                            lang={lang} 
                            onEditNickname={() => setIsEditingName(true)}
                            onAvatarChange={handleAvatarChange}
                            onBackgroundChange={handleBackgroundChange}
                            favoriteTeam={favoriteTeam}
                        />

                        <div className="mt-6">
                            {user.role === 'admin' && (
                                <MenuSection title={t(lang as Language, 'adminTools')} items={adminMenu} />
                            )}

                            <MenuSection title={t(lang as Language, 'agentTools')} items={agentMenu} />
                            <MenuSection title={t(lang as Language, 'supportInfo')} items={supportMenu} />
                            <MenuSection title={t(lang as Language, 'accountActions')} items={accountMenu} />

                            <div className="px-6 py-12 flex justify-center">
                                <button 
                                    onClick={() => setIsConfirmDeleteOpen(true)}
                                    className="text-[11px] font-bold text-slate-300 hover:text-rose-400 underline underline-offset-4 transition-colors"
                                >
                                    {t(lang as Language, 'deleteAccount')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <AppFooter lang={lang as any} />
                </>
            ) : (
                <DetailViewContainer 
                    title={
                        activeDetail === 'comments' ? t(lang as Language, 'recentActivity') :
                        activeDetail === 'leagues' ? t(lang as Language, 'favoriteLeagues') :
                        activeDetail === 'teams' ? t(lang as Language, 'favoriteTeams') :
                        activeDetail === 'team_detail' ? t(lang as Language, 'teamDetails') :
                        activeDetail === 'points' ? t(lang as Language, 'pointHistory') :
                        activeDetail === 'attendance' ? t(lang as Language, 'dailyAttendance') :
                        activeDetail === 'match_predictions' ? t(lang as Language, 'matchPredictions') :
                        activeDetail === 'notices' ? t(lang as Language, 'notices') :
                        activeDetail === 'sponsorship' ? t(lang as Language, 'sponsorship') :
                        t(lang as Language, 'language')
                    } 
                    onClose={() => {
                        if (activeDetail === 'team_detail') {
                            window.navigateTo('/mypage/teams');
                        } else {
                            window.navigateTo('/mypage');
                        }
                    }}
                    rightAction={
                        activeDetail === 'leagues' ? (
                            <button 
                                onClick={handleSaveLeagues}
                                disabled={isSavingLeagues || selectedLeagueIds.length === 0}
                                className="bg-slate-900 text-white px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg active:scale-95 disabled:opacity-30 transition-all flex items-center gap-2"
                            >
                                {isSavingLeagues ? <Loader2 size={14} className="animate-spin" /> : <><ShieldCheck size={14} /> {t(lang as Language, 'save')}</>}
                            </button>
                        ) : activeDetail === 'settings' ? (
                            <button 
                                onClick={handleSaveLanguage}
                                disabled={isSavingLang || tempLang === lang}
                                className="bg-slate-900 text-white px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg active:scale-95 disabled:opacity-30 transition-all flex items-center gap-2"
                            >
                                {isSavingLang ? <Loader2 size={14} className="animate-spin" /> : <><ShieldCheck size={14} /> {t(lang as Language, 'save')}</>}
                            </button>
                        ) : activeDetail === 'teams' ? (
                            <button 
                                onClick={handleSaveDefaultTeam}
                                disabled={isSavingDefaultTeam || !selectedDefaultTeamId}
                                className="bg-slate-900 text-white px-5 py-2 rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg active:scale-95 disabled:opacity-30 transition-all flex items-center gap-2"
                            >
                                {isSavingDefaultTeam ? <Loader2 size={14} className="animate-spin" /> : <><ShieldCheck size={14} /> {t(lang as Language, 'save')}</>}
                            </button>
                        ) : undefined
                    }
                >
                    {activeDetail === 'notices' && (
                        <NoticeListView />
                    )}
                    {activeDetail === 'sponsorship' && (
                        <SponsorshipView lang={lang as Language} />
                    )}
                    {activeDetail === 'attendance' && (
                        <AttendanceView userId={user.id} lang={lang as any} onPointsUpdate={setUserPoints} />
                    )}
                    {activeDetail === 'match_predictions' && (
                        <MyPredictionsView userId={user.id} lang={lang as any} onBack={() => setActiveDetail('none')} />
                    )}
                    {activeDetail === 'points' && (
                        <PointHistoryView userId={user.id} lang={lang as any} />
                    )}
                    {activeDetail === 'teams' && (
                        <FavoriteTeamsView 
                            userId={user.id} 
                            lang={lang as any} 
                            onSelectionChange={setSelectedDefaultTeamId}
                            onSaveTrigger={handleSaveDefaultTeam}
                        />
                    )}
                    {activeDetail === 'team_detail' && teamId && (
                        <FavoriteTeamDetailView teamId={teamId} lang={lang as any} />
                    )}
                    {activeDetail === 'comments' && (
                        <ActivityDashboard 
                            activities={userActivities} 
                            filterPeriod={filterPeriod} 
                            onPeriodChange={(p: FilterPeriodType) => setFilterPeriod(p)} 
                            isLoading={isDataLoading} 
                            lang={lang as any} 
                        />
                    )}
                    {activeDetail === 'leagues' && (
                        <LeagueSelector 
                            allLeagues={allLeagues} 
                            selectedIds={selectedLeagueIds} 
                            isLoading={isDataLoading} 
                            isSaving={isSavingLeagues} 
                            lang={lang as any} 
                            onToggle={(id) => { 
                                const up = id.toUpperCase(); 
                                setSelectedLeagueIds(prev => prev.includes(up) ? prev.filter(l => l !== up) : [...prev, up]); 
                            }} 
                            onSave={handleSaveLeagues} 
                        />
                    )}
                    {activeDetail === 'settings' && (
                        <div className="p-6 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="grid grid-cols-1 gap-4">
                                {[
                                    { id: 'en', name: 'English', sub: 'United Kingdom', flag: '🇬🇧' },
                                    { id: 'ko', name: '한국어', sub: '대한민국', flag: '🇰🇷' },
                                    // { id: 'ja', name: '日本語', sub: '日本', flag: '🇯🇵' },
                                    // { id: 'es', name: 'Español', sub: 'España', flag: '🇪🇸' },
                                    // { id: 'fr', name: 'Français', sub: 'France', flag: '🇫🇷' }
                                ].map((item) => (
                                    <button 
                                        key={item.id}
                                        onClick={() => setTempLang(item.id as Language)} 
                                        className={`group relative overflow-hidden flex items-center justify-between p-6 rounded-[2rem] border-2 transition-all duration-300 ${
                                            tempLang === item.id 
                                            ? 'border-slate-900 bg-slate-900 text-white shadow-2xl scale-[1.02]' 
                                            : 'border-slate-100 bg-white text-slate-900 hover:border-slate-200 hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-5">
                                            <span className="text-3xl filter drop-shadow-sm">{item.flag}</span>
                                            <div className="flex flex-col items-start">
                                                <span className="text-xl font-black tracking-tight">{item.name}</span>
                                                <span className={`text-xs font-bold uppercase tracking-wider ${tempLang === item.id ? 'text-slate-400' : 'text-slate-400'}`}>
                                                    {item.sub}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                                            tempLang === item.id 
                                            ? 'bg-white scale-100' 
                                            : 'bg-slate-100 scale-75 opacity-0 group-hover:opacity-100 group-hover:scale-100'
                                        }`}>
                                            <CheckCircle2 size={18} className={tempLang === item.id ? 'text-slate-900' : 'text-slate-300'} />
                                        </div>

                                        {tempLang === item.id && (
                                            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/5 rounded-full blur-2xl" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            <div className="p-6 rounded-3xl bg-slate-50 border border-slate-100">
                                <p className="text-xs text-slate-400 leading-relaxed font-medium">
                                    {t(lang as Language, 'languageAiNote')}
                                </p>
                            </div>
                        </div>
                    )}
                </DetailViewContainer>
            )}

            <EditNicknameModal 
                isOpen={isEditingName}
                onClose={() => setIsEditingName(false)}
                onSave={handleUpdateName}
                isLoading={isUpdatingName}
                initialName={user.name}
                lang={lang}
                error={nameError}
            />
        </div>
    );
};
